import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
/* eslint-disable complexity */
import { defineStore } from 'pinia';
import { useConfigProvider } from '../config-provider';
import { computed, reactive } from 'vue';
import { registerFunctionalColor, registerMiddleColors, registerPrimaryColor, registerCssVariables } from '../style/theme-helper';
import { TinyColor } from '@ctrl/tinycolor';
import { generateColors, isDarkColor } from '../utils/colors';
import cloneDeep from 'lodash/cloneDeep';
var themeCache = new Map();
var useThemeStore = defineStore('theme', function () {
  var configProvider = useConfigProvider();
  var theme = configProvider.theme;
  var _theme = reactive(theme);
  var getTheme = computed(function () {
    return _theme;
  });
  function fillColor(target, palettes) {
    target.bg = palettes[0];
    target['bg-hover'] = palettes[1];
    target['border'] = palettes[2];
    target['border-hover'] = palettes[3];
    target['hover'] = palettes[4];
    target['active'] = palettes[6];
    target['text'] = palettes[5];
    target['text-hover'] = palettes[4];
    target['text-active'] = palettes[6];
  }
  /**
   * 获取存储的主题配置项
   * @param target
   */
  function getThemeCache(target) {
    if (target === ':root') {
      return _theme;
    }
    var cache = themeCache.get(target);
    if (!cache) {
      cache = {
        color: {}
      };
      themeCache.set(target, cache);
    }
    return cache;
  }
  /**
   * 获取主题配置项
   * @param target
   * @param key
   */
  function getThemeItem(target, key) {
    var _theme$key;
    var theme = getThemeCache(target);
    theme[key] = (_theme$key = theme[key]) !== null && _theme$key !== void 0 ? _theme$key : {};
    return theme[key];
  }
  /**
   * 获取颜色
   * @param target
   * @param key
   */
  function getThemeColor(target, key) {
    var _theme$color$key;
    var theme = getThemeCache(target);
    theme.color[key] = (_theme$color$key = theme.color[key]) !== null && _theme$color$key !== void 0 ? _theme$color$key : cloneDeep(_theme.color[key]);
    return theme.color[key];
  }
  /**
   * 获取目标背景色
   * @param target
   */
  function getTargetBg(target) {
    var _ref, _ref2, _middle$bgContainer;
    var middle = getThemeColor(target, 'middle');
    var root = _theme.color.middle;
    return (_ref = (_ref2 = (_middle$bgContainer = middle['bg-container']) !== null && _middle$bgContainer !== void 0 ? _middle$bgContainer : middle['bg-base']) !== null && _ref2 !== void 0 ? _ref2 : root['bg-container']) !== null && _ref !== void 0 ? _ref : root['bg-base'];
  }
  /**
   * 设置功能色
   * @param type
   * @param color
   */
  function setFunctionalColor(type, colors) {
    var target = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ':root';
    var background = arguments.length > 3 ? arguments[3] : undefined;
    var _colors = getThemeColor(target, type);
    // const _colors = theme.color[type];
    var modifyColors = {};
    var contentBg = background !== null && background !== void 0 ? background : getTargetBg(target);
    if (colors.DEFAULT) {
      var palettes = generateColors(colors.DEFAULT, contentBg);
      modifyColors.DEFAULT = palettes[5];
      fillColor(modifyColors, palettes);
    }
    _extends(modifyColors, colors);
    _extends(_colors, modifyColors);
    if (target === ':root') {
      mountSideColors(true);
      mountHeadColors(true);
    }
    registerFunctionalColor(type, modifyColors, target, contentBg);
    return modifyColors;
  }
  /**
   * 设置默认色
   * @param color
   */
  function setPrimaryColor(colors) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var background = arguments.length > 2 ? arguments[2] : undefined;
    var primary = getThemeColor(target, 'primary');
    // const primary = theme.color.primary;
    var modifyColors = {};
    var contentBg = background !== null && background !== void 0 ? background : getTargetBg(target);
    if (colors.DEFAULT) {
      var palettes = generateColors(colors.DEFAULT, contentBg);
      modifyColors.DEFAULT = palettes[5];
      fillColor(modifyColors, palettes);
    }
    _extends(modifyColors, colors);
    _extends(primary, modifyColors);
    if (target === ':root') {
      var sidePrimary = getThemeColor('.stepin-side-theme', 'primary');
      _extends(sidePrimary, modifyColors);
      mountSideColors(true);
      var headPrimary = getThemeColor('.stepin-header-theme', 'primary');
      _extends(headPrimary, modifyColors);
      mountHeadColors(true);
    }
    registerPrimaryColor(modifyColors, target, contentBg);
    return modifyColors;
  }
  /**
   * 设置文本色及相关系列色
   * @param colors
   * @param target
   */
  function setTextSeriesColors(colors) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var middle = getThemeColor(target, 'middle');
    // const middle = theme.color.middle;
    var modifyColors = {};
    if (colors['text-base']) {
      var textBase = new TinyColor(colors['text-base']);
      var isDark = isDarkColor(colors['text-base']);
      // text colors
      var alphaRange = isDark ? [0.85, 0.65, 0.45, 0.25] : [1, 0.85, 0.65, 0.45];
      alphaRange.map(function (alpha) {
        return textBase.clone().setAlpha(alpha).toRgbString();
      }).forEach(function (color, i) {
        return modifyColors["text-".concat(i + 1)] = color;
      });
      modifyColors['text'] = modifyColors['text-1'];
      modifyColors['subtext'] = modifyColors['text-2'];
      modifyColors['disabled'] = modifyColors['text-4'];
      // bg color generate from text-base
      modifyColors['bg-spotlight'] = modifyColors['text-1'];
      // fill colors
      [0.15, 0.06, 0.04, 0.02].map(function (alpha) {
        return textBase.clone().setAlpha(alpha).toRgbString();
      }).forEach(function (color, i) {
        return modifyColors["fill-".concat(i + 1)] = color;
      });
      modifyColors['fill'] = modifyColors['fill-1'];
      // shadow colors
      modifyColors['shadow'] = textBase.clone().setAlpha(0.15).toRgbString();
      modifyColors['shadow-light'] = textBase.clone().setAlpha(0.1).toRgbString();
      // border colors
      modifyColors['border-1'] = textBase.clone().setAlpha(0.15).toRgbString();
      modifyColors['border-2'] = textBase.clone().setAlpha(0.06).toRgbString();
      modifyColors['border'] = modifyColors['border-1'];
    }
    if (colors['border-1']) {
      modifyColors['border'] = colors['border-1'];
    }
    _extends(modifyColors, colors);
    _extends(middle, modifyColors);
    registerMiddleColors(modifyColors, target);
    return modifyColors;
  }
  /**
   * 自适应调整功能色，包含主色
   * @param target
   */
  function adjustFunctionalColors() {
    var target = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ':root';
    var background = arguments.length > 1 ? arguments[1] : undefined;
    var primary = getThemeColor(target, 'primary');
    if (primary.DEFAULT) {
      setPrimaryColor({
        DEFAULT: primary.DEFAULT
      }, target, background);
    }
    var types = ['success', 'warning', 'error', 'info'];
    types.forEach(function (type) {
      var functional = getThemeColor(target, type);
      if (functional.DEFAULT) {
        setFunctionalColor(type, {
          DEFAULT: functional.DEFAULT
        }, target, background);
      }
    });
  }
  /**
   * 设置背景相关颜色
   * @param colors
   * @param target
   */
  function setBgSeriesColors(colors) {
    var _modifyColors$bgCont;
    var adjust = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var target = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ':root';
    var middle = getThemeColor(target, 'middle');
    // const middle = theme.color.middle;
    var modifyColors = {};
    var darken = function darken(color, amount) {
      return color.clone().darken(amount);
    };
    if (colors['bg-base']) {
      var bgBase = new TinyColor(colors['bg-base']);
      modifyColors['bg-container'] = "#".concat(bgBase.toHex());
      modifyColors['bg-container-light'] = "#".concat(darken(bgBase, 2).toHex());
      modifyColors['bg-popover'] = "#".concat(bgBase.toHex());
      modifyColors['bg-disabled'] = "#".concat(darken(bgBase, 4).toHex());
      modifyColors['bg-layout'] = "#".concat(darken(bgBase, 4).toHex());
      modifyColors['bg-side'] = "#".concat(bgBase.toHex());
      modifyColors['bg-header'] = "#".concat(bgBase.toHex());
    }
    if (colors['bg-container']) {
      modifyColors['bg-container-light'] = "#".concat(darken(new TinyColor(colors['bg-container']), 2).toHex());
    }
    _extends(modifyColors, colors);
    _extends(middle, modifyColors);
    registerMiddleColors(modifyColors, target);
    // 如果有修改侧边相关颜色，则重新挂载侧边颜色
    if (modifyColors['bg-side']) {
      mountSideColors(true);
    }
    // 如果有修改顶部相关颜色，则重新挂载顶部颜色
    if (modifyColors['bg-header']) {
      mountHeadColors(true);
    }
    // 自适应调整功能色及字体色
    var contentBg = (_modifyColors$bgCont = modifyColors['bg-container']) !== null && _modifyColors$bgCont !== void 0 ? _modifyColors$bgCont : modifyColors['bg-base'];
    if (adjust && contentBg) {
      var isDark = isDarkColor(contentBg);
      var textBase = isDark ? '#fff' : '#000';
      setTextSeriesColors({
        'text-base': textBase
      }, target);
      adjustFunctionalColors(target, contentBg);
    }
    return modifyColors;
  }
  /**
   * 挂载侧边颜色
   * @param adjust 是否自适应调整颜色，包括 字体颜色、主色、功能色等
   */
  function mountSideColors(adjust) {
    var middle = _theme.color.middle;
    var target = '.stepin-side-theme';
    var darkenColor = '#' + new TinyColor(middle['bg-side']).darken(1.5).toHex();
    registerMiddleColors({
      'bg-side': middle['bg-side'],
      'bg-menu': middle['bg-side'],
      'bg-container': middle['bg-side'],
      'bg-base': darkenColor,
      'bg-submenu': darkenColor
    }, target);
    if (adjust) {
      var isDark = isDarkColor(middle['bg-side']);
      var textBase = isDark ? '#fff' : '#000';
      setTextSeriesColors({
        'text-base': textBase
      }, target);
      adjustFunctionalColors(target, middle['bg-side']);
    }
  }
  /**
   * 挂载顶部颜色
   * @param adjust 是否自适应调整颜色，包括 字体颜色、主色、功能色等
   */
  function mountHeadColors(adjust) {
    var middle = _theme.color.middle;
    var target = '.stepin-header-theme';
    var darkenColor = '#' + new TinyColor(middle['bg-header']).darken(1.5).toHex();
    registerMiddleColors({
      'bg-header': middle['bg-header'],
      'bg-menu': middle['bg-header'],
      'bg-container': middle['bg-header'],
      'bg-base': darkenColor,
      'bg-submenu': darkenColor
    }, target);
    if (adjust) {
      var isDark = isDarkColor(middle['bg-header']);
      var textBase = isDark ? '#fff' : '#000';
      setTextSeriesColors({
        'text-base': textBase
      }, target);
      adjustFunctionalColors(target, middle['bg-header']);
    }
  }
  /**
   * 设置字体
   * @param font
   */
  function setFont(font) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _font = getThemeItem(target, 'font');
    // const _font = theme.font;
    if (font['font-size']) {
      var size = parseInt(font['font-size'].replace(/\D/g, ''));
      var step = 2;
      _font['font-size-xs'] = "".concat(Math.max(size - 2 * step, 10), "px");
      _font['font-size-sm'] = "".concat(Math.max(size - 1 * step, 10), "px");
      _font['font-size-lg'] = "".concat(Math.max(size + 1 * step, 10), "px");
      _font['font-size-xl'] = "".concat(Math.max(size + 2 * step, 10), "px");
      _font['font-size-xxl'] = "".concat(Math.max(size + 4 * step, 10), "px");
      _font['font-size-xxxl'] = "".concat(Math.max(size + 8 * step, 10), "px");
    }
    _extends(_font, font);
    registerCssVariables(_font, target);
  }
  function setPadding(padding) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _padding = getThemeItem(target, 'padding');
    // const _padding = theme.padding;
    if (padding['base']) {
      var step = parseInt(padding['base'].replace(/\D/g, ''));
      _padding['xxs'] = "".concat(step, "px");
      _padding['xs'] = "".concat(step * 2, "px");
      _padding['sm'] = "".concat(step * 3, "px");
      _padding['md'] = "".concat(step * 4, "px");
      _padding['lg'] = "".concat(step * 6, "px");
      _padding['xl'] = "".concat(step * 8, "px");
    }
    _extends(_padding, padding);
    var variables = Object.entries(_padding).filter(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
        k = _ref4[0],
        v = _ref4[1];
      return v !== null;
    }).reduce(function (p, _ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
        k = _ref6[0],
        v = _ref6[1];
      return (p["padding-".concat(k)] = v) && p;
    }, {});
    registerCssVariables(variables, target);
  }
  function setMargin(margin) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _margin = getThemeItem(target, 'margin');
    // const _margin = theme.margin;
    if (margin['base']) {
      var step = parseInt(margin['base'].replace(/\D/g, ''));
      _margin['xxs'] = "".concat(step, "px");
      _margin['xs'] = "".concat(step * 2, "px");
      _margin['sm'] = "".concat(step * 3, "px");
      _margin['md'] = "".concat(step * 4, "px");
      _margin['lg'] = "".concat(step * 6, "px");
      _margin['xl'] = "".concat(step * 8, "px");
    }
    _extends(_margin, margin);
    var variables = Object.entries(_margin).filter(function (_ref7) {
      var _ref8 = _slicedToArray(_ref7, 2),
        v = _ref8[1];
      return v !== null;
    }).reduce(function (p, _ref9) {
      var _ref10 = _slicedToArray(_ref9, 2),
        k = _ref10[0],
        v = _ref10[1];
      return (p["margin-".concat(k)] = v) && p;
    }, {});
    registerCssVariables(variables, target);
  }
  function setRadius(radius) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _radius = getThemeItem(target, 'radius');
    // const _radius = theme.radius;
    if (radius['radius-base']) {
      var step = parseInt(radius['radius-base'].replace(/\D/g, ''));
      _radius['radius-xs'] = "".concat(step, "px");
      _radius['radius-sm'] = "".concat(step * 2, "px");
      _radius['radius-md'] = "".concat(step * 3, "px");
      _radius['radius-lg'] = "".concat(step * 4, "px");
    }
    _extends(_radius, radius);
    registerCssVariables(_radius, target);
  }
  function setShadow(shadow) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _shadow = getThemeItem(target, 'shadow');
    // const _shadow = theme.shadow;
    _extends(_shadow, shadow);
    registerCssVariables(_shadow, target);
  }
  function setSize(size) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ':root';
    var _size = getThemeItem(target, 'size');
    // const _size = theme.size;
    _extends(_size, size);
    registerCssVariables(_size, target);
  }
  return {
    getTheme: getTheme,
    theme: _theme,
    setPrimaryColor: setPrimaryColor,
    setFunctionalColor: setFunctionalColor,
    setFont: setFont,
    setPadding: setPadding,
    setMargin: setMargin,
    setRadius: setRadius,
    setShadow: setShadow,
    setTextSeriesColors: setTextSeriesColors,
    setBgSeriesColors: setBgSeriesColors,
    setSize: setSize
  };
});
export default useThemeStore;